import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteCoachedStrengthWorkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { athlete } = useAthleteAuthContext();

  // Restore previous accordion state when returning
  const restoredWeeks = location.state?.weeks ?? null;
  const restoredOpenIndex = location.state?.openIndex ?? null;

  const weeksRef = useRef(restoredWeeks);
  const openIndexRef = useRef(restoredOpenIndex);

  const [weeks, setWeeks] = useState(restoredWeeks);
  const [openIndex, setOpenIndex] = useState(restoredOpenIndex);

  const [loading, setLoading] = useState(restoredWeeks ? false : true);
  const [error, setError] = useState(null);

  // ----------- DATE FORMAT ----------
  /*const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };*/

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    // Handle both YYYY-MM-DD and ISO format strings
    const dateOnly = dateString.split('T')[0]; // Remove time if present
    const [year, month, day] = dateOnly.split('-').map(Number);
    
    if (!year || !month || !day) return "";
    
    const date = new Date(year, month - 1, day);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const hasWeeks = (list) => Array.isArray(list) && list.length > 0;

  // ------------------------------------------------------------
  //  ⚡ FETCH ONLY IF NO RESTORED WEEKS (i.e., first page load)
  // ------------------------------------------------------------
  useEffect(() => {
    if (restoredWeeks) return;

    let mounted = true;

    const fetchCoachedData = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${API_URL.FRONTEND_COACHED_ATHLETE_STRENGTH_WORKOUT}${athlete.id}`,
          { headers: { Authorization: `Bearer ${athlete.token}` } },
        );

        const json = await response.json();
        if (!mounted) return;

        if (!response.ok) {
          setWeeks([]);
          setError(json?.message || "Failed to load coached strength workout");
          return;
        }

        let rawWeeks = json.data || [];

        console.log(rawWeeks);

        // Filter only valid weeks
        const filtered = rawWeeks.filter(
          (week) =>
            Array.isArray(week.days) &&
            week.days.some((d) => Array.isArray(d.items) && d.items.length > 0),
        );

        setWeeks(filtered);
        weeksRef.current = filtered;
      } catch (err) {
        if (mounted) setError(err.message || "Network error");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchCoachedData();
    return () => (mounted = false);
  }, [athlete?.id, athlete?.token, restoredWeeks]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const openDay = (weekId, day, items) => {
    navigate(`/athlete/coached/strength-workout/day`, {
      state: {
        weekId,
        day,
        date: day.date,
        items,
        openIndex,
        weeks: weeksRef.current, // ← restored instantly on back
      },
    });
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          <div>
            <AthleteHeader />
          </div>

          {/* LOADING */}
          {!restoredWeeks && loading && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-base font-medium text-gray-600">
                Loading coached strength workout...
              </p>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-base font-medium text-red-500">Error: {error}</p>
            </div>
          )}

          {/* CONTENT */}
          {!loading && !error && hasWeeks(weeks) && (
            <>
              <div className="pb-3 w-full max-w-2xl mx-auto flex flex-row justify-center items-center gap-10 px-5">
                <p className="w-full py-3 text-black text-sm xs:text-xl line-clamp-2 leading-tight text-center border-b border-black/30">
                  Coached Strength Workout
                </p>
              </div>

              <div className="w-full max-w-2xl mx-auto px-5">
                <div className="py-3">
                  <div className="flex justify-between gap-8 mb-4">
                    <button className="text-black border-b-2 border-yellow-600 text-base pb-2 font-bold">
                      Strength Workout
                    </button>
                  </div>
                </div>

                <div className="space-y-5">
                  {weeks.map((week, index) => {
                    const weekDate =
                      week.week_start_date?.substring(0, 10) ||
                      week.days?.[0]?.date ||
                      "";

                    return (
                      <div
                        key={week._id || index}
                        className={`${
                          openIndex === index && "!border-black/30"
                        } border border-transparent rounded-xl`}
                      >
                        <button
                          className="w-full flex justify-between items-center gap-5 text-white text-base font-bold bg-black text-left px-4 py-3 rounded-lg"
                          onClick={() => toggleAccordion(index)}
                        >
                          <span>
                            W{index + 1} | {formatDate(weekDate)}
                          </span>
                          <span className={`transform transition-transform`}>
                            {openIndex === index ? <Minus /> : <Plus />}
                          </span>
                        </button>

                        {openIndex === index && (
                          <div className="px-10 py-3">
                            {week.days
                              .filter(
                                (d) =>
                                  Array.isArray(d.items) && d.items.length > 0,
                              )
                              .map((day, i) => (
                                <div
                                  key={i}
                                  className="w-fit flex justify-between items-center"
                                >
                                  <button
                                    className="w-full py-1 text-left text-sm font-medium text-black capitalize border-b-2 border-transparent hover:border-darkYellow"
                                    onClick={() =>
                                      openDay(week._id, day, day.items)
                                    }
                                  >
                                   {day.label} - {formatDate(day.date)}  -  {" "}
                                   {day.training_focus || "Training Focus"}
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* EMPTY */}
          {!loading && !error && (!weeks || weeks.length === 0) && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-center text-black ">
                No coached strength workout found.
              </p>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};
